<template>
    <div id="fileviewer" :class="{ tiny: tiny }">
        <div id="content" class="flex-fill" ref="contentElement">
            <template v-if="isBusinessEntitiesRepositoryRegistered">
                <Form
                    v-if="artefact.mimetype === 'application/schema+json'"
                    v-model="content"
                    class="flex-fill"
                    @updating="(e: boolean) => (isUpdating = e)"
                    :isEditing="isEditing"
                />
                <DMN
                    v-else-if="artefact.mimetype === 'application/dmn+xml'"
                    v-model="artefact"
                    class="flex-fill"
                    @updating="(e: boolean) => (isUpdating = e)"
                    :isEditing="isEditing"
                />
                <img
                    v-else-if="contentFlavor === ContentFlavor.IMAGE"
                    class="flex-fill"
                    :src="blobUrl"
                />
                <div
                    v-else-if="contentFlavor === ContentFlavor.GOOGLEDRIVE"
                    class="gdrive flex-fill"
                    ref="gdriveElement"
                >
                    <iframe
                        border="0"
                        class="embed flex-fill"
                        :src="embedUrl"
                    />
                </div>
                <TextEditor
                    v-else-if="
                        isEditing && contentFlavor === ContentFlavor.TEXTUAL
                    "
                    ref="textEditorElement"
                    class="text-editor flex-fill"
                    v-model="artefact"
                    :readingMode="false"
                />
                <TextEditor
                    v-else-if="artefact.mimetype.includes('text/markdown')"
                    ref="textEditorElement"
                    class="text-editor flex-fill"
                    v-model="artefact"
                    :readingMode="true"
                />
                <div
                    v-else-if="artefact.mimetype === 'application/pdf'"
                    class="pdf flex-fill"
                >
                    <object :data="blobUrl" type="application/pdf">
                        <div class="button">
                            <Button
                                class="theme-change p-button p-component"
                                icon="pi pi-download"
                                label="Download PDF"
                                @click="download"
                            />
                            <p>
                                <small>
                                    Your browser doesn't support PDF preview.
                                    Try another browser or download the PDF to
                                    view it on your computer.
                                </small>
                            </p>
                        </div>
                    </object>
                </div>
                <highlightjs
                    v-else-if="contentFlavor === ContentFlavor.TEXTUAL"
                    :language="language"
                    :code="content"
                    class="any flex-fill"
                />
                <div v-else class="flex-fill">
                    Cannot show
                    <span style="font-weight: bold">{{
                        artefact.mimetype
                    }}</span>
                    file type.
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import TextEditor from "@/components/TextEditor.vue";
import { mime, downloadBlob } from "@/utils";
import DMN from "@/components/DMN.vue";
import Form from "@/components/Form.vue";
import hljs from "highlight.js";
import { PropType, ref, computed, onUpdated, toRef, watchEffect } from "vue";
import { useRoute } from "vue-router";
import { useEditManager } from "@/composables/edit-manager";
import { useRepository } from "@/composables/repository";
import type { Artefact } from "@/lib/artefacts";
import { get_mimetype_flavor, ContentFlavor } from "@/lib/contentFlavor";

const props = defineProps({
    hideHistory: {
        type: Boolean,
        default: false,
    },
    tiny: {
        type: Boolean,
        default: false,
    },
    showToastOnSave: {
        type: Boolean,
        default: true,
    },
    newIfNotExists: {
        type: Boolean,
        default: false,
    },
    artefact: {
        type: Object as PropType<Artefact>,
        required: true,
    },
});

// This repository is required for the text editor to correctly show elements
// related to the business entities.
const { isRegistered: isBusinessEntitiesRepositoryRegistered } =
    useRepository("businessEntities");

const artefact = toRef(() => props.artefact);
const route = useRoute();
const { isEditing } = useEditManager();

const isUpdating = ref(false);

const content = ref("");
const blobUrl = ref("");

const contentElement = ref<HTMLElement>();
const gdriveElement = ref<HTMLElement>();

const contentFlavor = computed(() => {
    return get_mimetype_flavor(artefact.value?.mimetype);
});

const language = computed(() => mime.getExtension(artefact.value.mimetype));

const embedUrl = computed((): string => {
    if (contentFlavor.value === ContentFlavor.GOOGLEDRIVE) {
        const obj = JSON.parse(content.value);
        return isEditing.value ? obj.url : obj.embedUrl;
    } else if (artefact.value?.mimetype === "application/pdf") {
        return blobUrl.value;
    }
    return "";
});

/**
 * Dowload the file using a dataurl link
 */
const download = async () => {
    const extension = mime.getExtension(artefact.value.mimetype);
    downloadBlob(blobUrl.value, `${artefact.value.name}.${extension}`);
};

// Get file contents as string or dataURL (in case of being an image)
const setFileContents = async (artefact_: Artefact) => {
    content.value = artefact_.content;
    blobUrl.value = await artefact_.getDataUrl();
};

onUpdated(() => {
    if (
        artefact.value?.mimetype === "text/markdown" &&
        !isEditing.value &&
        contentElement.value
    ) {
        contentElement.value
            .querySelectorAll<HTMLElement>("pre code:not(.hljs)")
            .forEach(hljs.highlightBlock);
        if (route.hash) {
            const anchor = document.querySelector(`a[href='${route.hash}']`);
            if (anchor) {
                anchor.scrollIntoView();
            }
        }
    }
});

watchEffect(async () => {
    if (artefact.value) {
        await setFileContents(artefact.value);
    } else {
        content.value = "";
        blobUrl.value = "";
    }
});
</script>

<style scoped lang="sass">
// Import is somewhat deprecated, but use is not yet supported here...
// Shared markdown styling between viewer and editor
@import '@/shared/styles/markdown'

.flex-fill
    display: flex
    flex-direction: column

    > *
        flex: 1

.actions
    flex: 0

#fileviewer
    flex: 1
    display: flex
    flex-direction: column

    position: relative
    max-width: 100vw

    .filepath
        align-items: center
        gap: 0.2rem

        .title
            margin-right: 1rem
            margin-bottom: 0
            &[contenteditable="true"]
                outline: 1px ridge
                outline-color: var(--stekz-primary-color)

        .edit
            box-shadow: none !important
            opacity: 0
            transform: translateX(-2rem)
            transition: ease-in-out 150ms all
        &:hover, &:focus, &:active
            .edit
                opacity: 1
                transform: translateX(0)

    :deep(.p-toggleable-content)
        display: flex
        flex-direction: column
        flex: 1

        .p-panel-content
            min-height: 100px
            display: flex
            flex-direction: column
            flex: 1

    #content
        flex: 1
        padding: 0.25rem
        overflow-x: auto
        height: 100%

        .text-editor
            padding: 0.5em 1em

            // Make the editor scroll by itsel
            :deep(c-text-editor::part(editor-document))
                overflow-y: auto

            :deep(c-text-editor::part(main))
                position: absolute
                inset: 0

        :deep(.markdown)
            display: flex
            flex: 1
            flex-direction: column

            c-text-editor
                display: flex
                flex: 1
                flex-direction: column

        .notfound
            font-style: italic
            color: darkgrey
            text-align: center

        .pdf
            object, iframe
                border: 0
                width: 100%
                height: 90vh
                text-align: center

            .button
                max-width: 400px
                margin: auto

        .gdrive
            text-align: center

            .embed
                border: 0
                width: 100%
                height: 90vh
            a
                text-decoration: none

        // Disable top margin for code files
        :deep(> div > pre)
            margin: 0

    &.tiny
        border-top: 1px solid #dee2e6

        :deep(.p-panel-header)
            display: none

    &.has-gdrive
        border-top: 1px solid #dee2e6

        :deep(.p-panel-content, #content)
            padding: 0
</style>

<style lang="css">
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn.css");
</style>
